import React from "react";
import {
  CloseOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
} from "@ant-design/icons";

import axiosInstance from "../../helpers/axiosConfig";
import { file } from "../../static/api";
import { setImages } from "../../store/slices/foldersSlice";
import { useDispatch, useSelector } from "react-redux";
import { FoldersSelector } from "../../store/selectors";
import { useLocation } from "react-router";

import "./style.css";

export const ImageMask = ({ url, name, close, id, deg, list = false, mimeType, width, height, template }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { folderImages } = useSelector(FoldersSelector);

  const [rotation, setRotation] = React.useState(deg);
  const isVideo = mimeType && mimeType.startsWith('video/');
  
  // Get template from prop or default to 1up
  const currentTemplate = template || "1up";

  const imgRef = React.useRef();

  const rotateRight = () => {
    let newRotation = rotation + 90;
    if (newRotation >= 360) {
      newRotation = 0;
    }
    setRotation((prevState) => newRotation);
  };

  const rotateLeft = () => {
    let newRotation = rotation - 90;
    if (newRotation <= -360) {
      newRotation = 0;
    }
    setRotation((prevState) => newRotation);
  };

  const handleCloseRef = React.useRef();
  
  const handleClose = React.useCallback(async () => {
    if (!list && location.pathname.includes("/dashboard/")) {
      await axiosInstance.patch(`${file}/${id}`, {
        rotation: rotation,
      });

      const newArr = folderImages.map((item) => {
        if (item.id === id) {
          return { ...item, rotation: rotation };
        } else {
          return item;
        }
      });
      dispatch(setImages(newArr));
    }

    close();
  }, [list, location.pathname, id, rotation, folderImages, dispatch, close]);

  handleCloseRef.current = handleClose;

  // Handle keyboard dismissal and click outside
  React.useEffect(() => {
    const handleKeyDown = () => {
      if (handleCloseRef.current) {
        handleCloseRef.current();
      }
    };

    const handleClick = (e) => {
      if (e.target.classList.contains('mask_wrapper')) {
        if (handleCloseRef.current) {
          handleCloseRef.current();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('click', handleClick);
    };
  }, []);

  const handleImageLoad = (e) => {
    const img = e.target;
    const imgWidth = width || img.naturalWidth;
    const imgHeight = height || img.naturalHeight;
    
    if (imgWidth && imgHeight) {
      const aspectRatio = imgWidth / imgHeight;
      if (aspectRatio > 1.1) {
        img.classList.add('mask__content--landscape');
      } else if (aspectRatio < 0.9) {
        img.classList.add('mask__content--portrait');
      } else {
        img.classList.add('mask__content--square');
      }
    }
  };

  const handleVideoLoad = (e) => {
    const video = e.target;
    const videoWidth = width || video.videoWidth;
    const videoHeight = height || video.videoHeight;
    
    if (videoWidth && videoHeight) {
      const aspectRatio = videoWidth / videoHeight;
      if (aspectRatio > 1.1) {
        video.classList.add('mask__content--landscape');
      } else if (aspectRatio < 0.9) {
        video.classList.add('mask__content--portrait');
      } else {
        video.classList.add('mask__content--square');
      }
    }
  };

  const renderMediaElement = (additionalClassName = "", additionalStyle = {}, useRef = false) => {
    const baseStyle = { transform: `rotate(${rotation}deg)`, ...additionalStyle };
    const className = `mask__template-item ${additionalClassName}`.trim();
    
    if (isVideo) {
      return (
        <video
          {...(useRef ? { ref: imgRef } : {})}
          src={url}
          className={className}
          style={baseStyle}
          onLoadedMetadata={handleVideoLoad}
          controls
          autoPlay
          muted
          playsInline
        />
      );
    } else {
      return (
        <img
          {...(useRef ? { ref: imgRef } : {})}
          src={url}
          alt={name}
          className={className}
          style={baseStyle}
          onLoad={handleImageLoad}
        />
      );
    }
  };

  const renderTemplatePreview = () => {
    const baseClassName = "mask__template";
    const templateClassName = `${baseClassName} ${baseClassName}--${currentTemplate}`;
    
    // Render based on template
    if (currentTemplate === "1up") {
      return (
        <div className={templateClassName}>
          {renderMediaElement("", {}, true)}
        </div>
      );
    } else if (currentTemplate === "n2Up") {
      return (
        <div className={templateClassName}>
          <div className="mask__template-item-wrapper mask__template-item-wrapper--left">
            {renderMediaElement()}
          </div>
          <div className="mask__template-item-wrapper mask__template-item-wrapper--right">
            {renderMediaElement()}
          </div>
        </div>
      );
    } else if (currentTemplate === "n2Up-mirror-left") {
      return (
        <div className={templateClassName}>
          <div className="mask__template-item-wrapper mask__template-item-wrapper--left">
            {renderMediaElement("mask__template-item--mirror-left")}
          </div>
          <div className="mask__template-item-wrapper mask__template-item-wrapper--right">
            {renderMediaElement()}
          </div>
        </div>
      );
    } else if (currentTemplate === "n2Up-mirror-right") {
      return (
        <div className={templateClassName}>
          <div className="mask__template-item-wrapper mask__template-item-wrapper--left">
            {renderMediaElement()}
          </div>
          <div className="mask__template-item-wrapper mask__template-item-wrapper--right">
            {renderMediaElement("mask__template-item--mirror-right")}
          </div>
        </div>
      );
    } else if (currentTemplate === "4up") {
      return (
        <div className={templateClassName}>
          {[0, 1, 2, 3].map((idx) => (
            <div key={idx} className="mask__template-item-wrapper">
              {renderMediaElement()}
            </div>
          ))}
        </div>
      );
    } else if (currentTemplate === "4up-warhol") {
      return (
        <div className={templateClassName}>
          {[0, 1, 2, 3].map((idx) => (
            <div key={idx} className="mask__template-item-wrapper">
              {renderMediaElement(`mask__template-item--warhol mask__template-item--warhol-${idx}`)}
            </div>
          ))}
        </div>
      );
    } else if (currentTemplate === "2next") {
      return (
        <div className={templateClassName}>
          <div className="mask__template-item-wrapper mask__template-item-wrapper--left">
            {renderMediaElement()}
          </div>
          <div className="mask__template-item-wrapper mask__template-item-wrapper--right">
            {renderMediaElement()}
          </div>
        </div>
      );
    } else if (currentTemplate === "4next") {
      return (
        <div className={templateClassName}>
          {[0, 1, 2, 3].map((idx) => (
            <div key={idx} className="mask__template-item-wrapper">
              {renderMediaElement()}
            </div>
          ))}
        </div>
      );
    }
    
    // Fallback to 1up
    return (
      <div className={templateClassName}>
        {renderMediaElement()}
      </div>
    );
  };

  return (
    <div className={"mask_wrapper"} onClick={handleClose}>
      <div className="mask__content" onClick={(e) => e.stopPropagation()}>
        {renderTemplatePreview()}
      </div>

      <ul className={"mask__top_panel"} onClick={(e) => e.stopPropagation()}>
        {!list && location.pathname.includes("/dashboard/") && (
          <>
            <li className={"top_panel__item"}>
              <RotateLeftOutlined
                className={"item__icon"}
                onClick={rotateLeft}
              />
            </li>
            <li className={"top_panel__item"} onClick={rotateRight}>
              <RotateRightOutlined className={"item__icon"} />
            </li>
          </>
        )}
        <li className={"top_panel__item"} onClick={handleClose}>
          <CloseOutlined className={"item__icon"} />
        </li>
      </ul>
    </div>
  );
};
